import { defineMessages } from "react-intl";

/** Section-level strings: the three list titles and their cross links. */
export const sectionMessages = defineMessages({
  sectionTitle: {
    defaultMessage: "Fitment Management",
    id: "4vrPUG",
    description: "fitment section, window title",
  },
  schemas: {
    defaultMessage: "Schemas",
    id: "OuZ85P",
    description: "fitment section, schemas list title and link",
  },
  axes: {
    defaultMessage: "Axes",
    id: "BzjX2G",
    description: "fitment section, axes list title and link",
  },
  qualifiers: {
    defaultMessage: "Qualifiers",
    id: "/j1RXx",
    description: "fitment section, qualifiers list title and link",
  },
  applications: {
    defaultMessage: "Applications",
    id: "CJ0tYT",
    description: "fitment section, applications list title and link",
  },
  loadError: {
    defaultMessage: "This list did not load. Check the connection and retry.",
    id: "XXapCl",
    description: "fitment section, a vocabulary list (axes, schemas, qualifiers) failed to load",
  },
});

export const schemaMessages = defineMessages({
  create: {
    defaultMessage: "Create schema",
    id: "Lk/WH8",
    description: "fitment schema, create button and page title",
  },
  empty: {
    defaultMessage:
      "No schemas yet. A schema is what a shopper picks a vehicle by: Year, Make, Model.",
    id: "7bd/1v",
    description: "fitment schema list, empty state",
  },
  colName: {
    defaultMessage: "Schema",
    id: "8CdRRC",
    description: "fitment schema list column",
  },
  colAxes: {
    defaultMessage: "Axes, in order",
    id: "dUkpvg",
    description: "fitment schema list column",
  },
  colFlags: {
    defaultMessage: "Shown as",
    id: "CQOCJp",
    description: "fitment schema list column",
  },
  generalTitle: {
    defaultMessage: "General information",
    id: "kZaJC4",
    description: "fitment schema detail, card title",
  },
  name: {
    defaultMessage: "Name",
    id: "mVwCL8",
    description: "fitment field",
  },
  displayLabel: {
    defaultMessage: "Display label",
    id: "qVQgWl",
    description: "fitment field",
  },
  displayLabelHelp: {
    defaultMessage: "What a shopper sees. Left blank, the name is shown.",
    id: "eA33sN",
    description: "fitment field help",
  },
  axesTitle: {
    defaultMessage: "Axes",
    id: "UPJ7ka",
    description: "fitment schema detail, card title",
  },
  axesSubtitle: {
    defaultMessage:
      "The order here is the order a shopper is asked. An axis an application already uses cannot be removed.",
    id: "HEm5iv",
    description: "fitment schema detail, card subtitle",
  },
  axesEmpty: {
    defaultMessage: "No axes yet. Add one below.",
    id: "b/+6/0",
    description: "fitment schema detail, empty axes",
  },
  addAxis: {
    defaultMessage: "Add axis",
    id: "2aWNjH",
    description: "fitment schema detail, add axis picker",
  },
  noAxesLeft: {
    defaultMessage: "Every axis is already in this schema. Create more under Axes.",
    id: "gnjG9j",
    description: "fitment schema detail, add axis picker help",
  },
  moveUp: {
    defaultMessage: "Move up",
    id: "jre+et",
    description: "fitment schema detail, axis row button",
  },
  moveDown: {
    defaultMessage: "Move down",
    id: "1b3T4L",
    description: "fitment schema detail, axis row button",
  },
  remove: {
    defaultMessage: "Remove",
    id: "kQ+C6+",
    description: "fitment schema detail, axis row button",
  },
  visibilityTitle: {
    defaultMessage: "Where it shows",
    id: "XDXOQ8",
    description: "fitment schema detail, sidebar card title",
  },
  isActive: {
    defaultMessage: "Active",
    id: "15KcNG",
    description: "fitment schema flag",
  },
  isActiveHelp: {
    defaultMessage: "Shoppers can pick by this schema.",
    id: "fG+k/9",
    description: "fitment schema flag help",
  },
  showAsFacet: {
    defaultMessage: "Show as facet",
    id: "4C5T6S",
    description: "fitment schema flag",
  },
  showAsFacetHelp: {
    defaultMessage: "Offered as a filter in search results.",
    id: "7CTMXx",
    description: "fitment schema flag help",
  },
  searchableByKeyword: {
    defaultMessage: "Searchable by keyword",
    id: "urRAdS",
    description: "fitment schema flag",
  },
  searchableByKeywordHelp: {
    defaultMessage: "A typed search matches its values.",
    id: "QT9qc1",
    description: "fitment schema flag help",
  },
  inactive: {
    defaultMessage: "Inactive",
    id: "R7gpPL",
    description: "fitment schema list flag pill",
  },
  facet: {
    defaultMessage: "Facet",
    id: "Up77JV",
    description: "fitment schema list flag pill",
  },
  keyword: {
    defaultMessage: "Keyword",
    id: "hn3SOA",
    description: "fitment schema list flag pill",
  },
  viewApplications: {
    defaultMessage: "Applications",
    id: "o47MXf",
    description: "fitment schema detail, button to the applications list",
  },
  deleteTitle: {
    defaultMessage: "Delete schema",
    id: "Lzloye",
    description: "fitment schema detail, dialog title",
  },
  deleteContent: {
    defaultMessage: "Delete {name}? It is refused while any application or product still uses it.",
    id: "jB9OSx",
    description: "fitment schema detail, dialog content",
  },
});

export const axisMessages = defineMessages({
  create: {
    defaultMessage: "Create axis",
    id: "Uc/2jg",
    description: "fitment axis, create button and page title",
  },
  empty: {
    defaultMessage: "No axes yet. An axis is one dimension of fit: Year, Make, Model.",
    id: "+Oe4UF",
    description: "fitment axis list, empty state",
  },
  colName: {
    defaultMessage: "Axis",
    id: "LH2DCn",
    description: "fitment axis list column",
  },
  colDisplayLabel: {
    defaultMessage: "Display label",
    id: "owy+R0",
    description: "fitment axis list column",
  },
  colValueKind: {
    defaultMessage: "Values are",
    id: "ajrHzJ",
    description: "fitment axis list column",
  },
  valueKind: {
    defaultMessage: "Values are",
    id: "IA+/p4",
    description: "fitment axis field",
  },
  valueKindHelp: {
    defaultMessage: "Only decides how values sort: numbers by size, text alphabetically.",
    id: "Wc4Np/",
    description: "fitment axis field help",
  },
  valueKindText: {
    defaultMessage: "Text",
    id: "yYmhgj",
    description: "fitment axis value kind",
  },
  valueKindNumber: {
    defaultMessage: "Numbers",
    id: "99Wcmf",
    description: "fitment axis value kind",
  },
  valuesTitle: {
    defaultMessage: "Values",
    id: "ZJIMDj",
    description: "fitment axis detail, values card title",
  },
  deleteTitle: {
    defaultMessage: "Delete axis",
    id: "lm5hrd",
    description: "fitment axis detail, dialog title",
  },
  deleteContent: {
    defaultMessage:
      "Delete {name} and its values? It is refused while a schema still has it or an application uses one of its values.",
    id: "pYObvd",
    description: "fitment axis detail, dialog content",
  },
});

export const qualifierMessages = defineMessages({
  create: {
    defaultMessage: "Create qualifier",
    id: "dGYyXy",
    description: "fitment qualifier, create button and page title",
  },
  empty: {
    defaultMessage:
      "No qualifiers yet. A qualifier narrows a fit without being an axis: Cab = Crew Cab.",
    id: "ktqf5y",
    description: "fitment qualifier list, empty state",
  },
  colName: {
    defaultMessage: "Qualifier",
    id: "kQjhUt",
    description: "fitment qualifier list column",
  },
  colDisplayLabel: {
    defaultMessage: "Display label",
    id: "i8NzRe",
    description: "fitment qualifier list column",
  },
  colValues: {
    defaultMessage: "Values",
    id: "X4CZNa",
    description: "fitment qualifier list column",
  },
  valuesTitle: {
    defaultMessage: "Values",
    id: "aVvk2J",
    description: "fitment qualifier detail, values card title",
  },
  deleteTitle: {
    defaultMessage: "Delete qualifier",
    id: "D8IveX",
    description: "fitment qualifier detail, dialog title",
  },
  deleteContent: {
    defaultMessage:
      "Delete {name} and its values? It is refused while an application or a product still uses one.",
    id: "uMe8pa",
    description: "fitment qualifier detail, dialog content",
  },
});

/** The values card shared by axis and qualifier detail. */
export const valueMessages = defineMessages({
  add: {
    defaultMessage: "Add values",
    id: "2IAP46",
    description: "fitment values card, button and dialog title",
  },
  addHelp: {
    defaultMessage: "One value per line. A value already on the list is left as it is.",
    id: "yh1skz",
    description: "fitment values card, add dialog help",
  },
  addLineError: {
    defaultMessage: "Line {line} ({value}): {message}",
    id: "MFcFbg",
    description: "fitment values card, add dialog, an error on one of the values sent",
  },
  addCount: {
    defaultMessage: "{count, plural, one {# value added} other {# values added}}",
    id: "wTw+QH",
    description: "fitment values card, notification",
  },
  empty: {
    defaultMessage: "No values yet",
    id: "zLeGlX",
    description: "fitment values card, empty state",
  },
  search: {
    defaultMessage: "Search values",
    id: "9gqdFg",
    description: "fitment values card, search input label",
  },
  noMatches: {
    defaultMessage: "No values match",
    id: "VsKzAB",
    description: "fitment values card, empty state while searching",
  },
  colValue: {
    defaultMessage: "Value",
    id: "BBsX19",
    description: "fitment values card column",
  },
  rename: {
    defaultMessage: "Rename",
    id: "ZjfFbU",
    description: "fitment values card, row button",
  },
  delete: {
    defaultMessage: "Delete",
    id: "9h8GX/",
    description: "fitment values card, row button",
  },
  deleteTitle: {
    defaultMessage: "Delete value",
    id: "ZNb1++",
    description: "fitment values card, dialog title",
  },
  deleteContent: {
    defaultMessage: "Delete {value}? It is refused while an application still uses it.",
    id: "Z/3eEX",
    description: "fitment axis values card, dialog content",
  },
  qualifierValueDeleteContent: {
    defaultMessage:
      "Delete {value}? It is refused while an application or a product still uses it.",
    id: "d+9trz",
    description: "fitment qualifier values card, dialog content",
  },
  savedBefore: {
    defaultMessage: "Save the {kind} first, then add its values here.",
    id: "Pp9QOr",
    description: "fitment values card, on a create screen",
  },
});

export const applicationMessages = defineMessages({
  title: {
    defaultMessage: "{schema} applications",
    id: "GoiIl1",
    description: "fitment applications list, page title",
  },
  add: {
    defaultMessage: "Add application",
    id: "i8Jjma",
    description: "fitment applications list, button and dialog title",
  },
  addHelp: {
    defaultMessage:
      "Pick one value per axis; leave an axis blank to fit every value on it. A typed value the axis does not have yet is added to it.",
    id: "uj6sTP",
    description: "fitment applications add dialog, help",
  },
  addQualifiersHelp: {
    defaultMessage: "Optional. A qualifier narrows this point: Cab = Crew Cab.",
    id: "kMKtWu",
    description: "fitment applications add dialog, qualifiers help",
  },
  addNothing: {
    defaultMessage: "Pick at least one value",
    id: "4Q9x0V",
    description: "fitment applications add dialog, validation",
  },
  empty: {
    defaultMessage: "No applications yet",
    id: "2kB2pN",
    description: "fitment applications list, empty state",
  },
  colQualifiers: {
    defaultMessage: "Qualifiers",
    id: "54VUFW",
    description: "fitment applications list column",
  },
  any: {
    defaultMessage: "any",
    id: "gHhXwd",
    description: "fitment applications list, an unconstrained axis",
  },
  deleteTitle: {
    defaultMessage: "Delete application",
    id: "aU7hhN",
    description: "fitment applications list, dialog title",
  },
  deleteContent: {
    defaultMessage: "Delete {name}? It is refused while a product still fits it.",
    id: "ThKvCQ",
    description: "fitment applications list, dialog content",
  },
  count: {
    defaultMessage: "{count, plural, one {# application} other {# applications}}",
    id: "ZVZVuc",
    description: "fitment applications list, subtitle",
  },
});
